import React, { useState } from 'react';
import { useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

const FAQSection: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]); // Start with all closed

  // Set default open items based on screen size
  useEffect(() => {
    const setDefaultOpenItems = () => {
      if (window.innerWidth >= 1024) {
        // Desktop: Open first question in each column (1st and 2nd)
        setOpenItems([1, 2]);
      } else {
        // Mobile: Open only first question
        setOpenItems([1]);
      }
    };

    setDefaultOpenItems();

    // Listen for window resize to adjust if needed
    window.addEventListener('resize', setDefaultOpenItems);
    return () => window.removeEventListener('resize', setDefaultOpenItems);
  }, []);

  const faqItems: FAQItem[] = [
    {
      id: 1,
      question: "What will this actually do for us?",
      answer: "Find the right prospects, start real conversations, and book qualified meetings."
    },
    {
      id: 2,
      question: "Will it sound like us?",
      answer: "Yes, human and on-brand. It mirrors your voice and message."
    },
    {
      id: 3,
      question: "Does it work with our tools?",
      answer: "It replaces most tools, just connect your Calendar + LinkedIn; CRM is optional."
    },
    {
      id: 4,
      question: "Is it compliant and secure?",
      answer: "Yes, your data is protected, opt-outs and local rules are respected, and you can turn off access anytime."
    },
    {
      id: 5,
      question: "Who owns the data?",
      answer: "You do. All contacts, messages, and results are yours."
    },
    {
      id: 6,
      question: "Where do the leads come from?",
      answer: "From 12+ trusted sources; decision-makers are verified before outreach."
    },
    {
      id: 7,
      question: "How much work is this for our team?",
      answer: "Very little, we set it up; you launch and run with a few clicks."
    },
    {
      id: 8,
      question: "Do we stay in control?",
      answer: "Yes, approve the tone and turn agents or channels on/off anytime."
    },
    {
      id: 9,
      question: "Which channels are covered?",
      answer: "Lead Gen, Calls, WhatsApp, Email, LinkedIn"
    },
    {
      id: 10,
      question: "Will it work for our industry and buyers?",
      answer: "Yes, will be tailored to your market, roles, regions, and goals."
    }
  ];

  const toggleItem = (id: number) => {
    setOpenItems(prev => 
      prev.includes(id) 
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id]
    );
  };

  const isOpen = (id: number) => openItems.includes(id);

  return (
    <section className="relative py-4 sm:py-8 lg:py-4 px-4 sm:px-6">
      <div className="w-full max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-text-primary font-black text-2xl sm:text-3xl lg:text-4xl xl:text-5xl mb-3 sm:mb-4 tracking-tight">
            Your Questions, Answered
          </h2>
          <div className="w-16 sm:w-20 lg:w-24 h-1 bg-gradient-brand mx-auto"></div>
        </div>

        {/* FAQ Grid */}
        <div className="max-w-6xl mx-auto">
          {/* Desktop: 2 columns, 5 rows */}
          <div className="hidden lg:grid lg:grid-cols-2 lg:gap-4">
            {faqItems.map((item) => (
              <div
                key={item.id}
                className="bg-bg-surface border border-card-border rounded-xl p-6 shadow-glow-combined card-inner-glow transition-all duration-300 hover:shadow-glow-blue"
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full flex items-center justify-between text-left group"
                >
                  <h3 className="text-text-primary font-semibold text-lg pr-4 group-hover:text-brand-blue transition-colors duration-300">
                    {item.question}
                  </h3>
                  <div className="flex-shrink-0 w-6 h-6 text-brand-blue group-hover:text-brand-green-teal transition-colors duration-300">
                    {isOpen(item.id) ? (
                      <ChevronUp className="w-6 h-6" />
                    ) : (
                      <ChevronDown className="w-6 h-6" />
                    )}
                  </div>
                </button>
                
                {/* Answer */}
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  isOpen(item.id) ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
                }`}>
                  <div className="text-text-secondary text-base leading-relaxed">
                    {item.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: 1 column */}
          <div className="lg:hidden space-y-3">
            {faqItems.map((item) => (
              <div
                key={item.id}
                className="bg-bg-surface border border-card-border rounded-xl p-4 sm:p-6 shadow-glow-combined card-inner-glow transition-all duration-300 hover:shadow-glow-blue"
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full flex items-center justify-between text-left group"
                >
                  <h3 className="text-text-primary font-semibold text-base sm:text-lg pr-4 group-hover:text-brand-blue transition-colors duration-300">
                    {item.question}
                  </h3>
                  <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 text-brand-blue group-hover:text-brand-green-teal transition-colors duration-300">
                    {isOpen(item.id) ? (
                      <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6" />
                    ) : (
                      <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />
                    )}
                  </div>
                </button>
                
                {/* Answer */}
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  isOpen(item.id) ? 'max-h-96 opacity-100 mt-3 sm:mt-4' : 'max-h-0 opacity-0'
                }`}>
                  <div className="text-text-secondary text-sm sm:text-base leading-relaxed">
                    {item.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;